/* eslint-disable max-lines */
import { pendingChipMetaAtom, pendingEntryMethodAtom } from '@/atoms/chatLaunchAtoms';
import {
  floatingAskHeidiInputVisibleAtom,
  floatingAskHeidiPrefillAtom,
} from '@/atoms/floatingAskHeidiAtoms';
import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { floatingBarFocusTriggerAtom } from '@/atoms/selectedNoteTextAtoms';
import { SourcesButton } from '@/components/chat/components/SourcesButton';
import { inlineSelectionEditActiveAtom } from '@/components/tiptap/shared/inline-selection-edit/atoms';
import { renderPlaceholderSlots } from '@/components/ui/composer-placeholder-slots';
import DictateButton from '@/components/ui/dictate-button';
import type { SuggestedChip } from '@/components/ui/transcript-chips/types';
import { useWhiteLabelSafe } from '@/context/white-label';
import {
  useIsAskHeidiSourcesShortcutsEnabled,
  useIsDictateReengagementEnabled,
} from '@/features/flags';
import {
  IS_ASK_HEIDI_SHORTCUTS_ENABLED,
  IS_TRANSCRIPT_CHIPS_ENABLED,
} from '@/features/flags/legacy-flags';
import { useManualExposure } from '@/hooks/amplitude/use-manual-exposure';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { composeSelectedTextQuestionPrompt } from '@/lib/selection-ai-prompts';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  trackDictateReengagementViewed,
  useDictateReengagementViewExposure,
} from '@/models/analytics/features/dictate-reengagement-analytics';
import type { EvidenceAskHeidiSurface } from '@/models/analytics/features/evidence-shortcuts';
import { isComposingEvent } from '@/utils/keyboardEventHelpers';
import { VoiceAssistantTrigger } from '@heidi/agents';
import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { DictateShortcutTooltipContent } from '@heidi/dictate';
import { isEditorLive } from '@heidi/editor/core';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowUp, Lightbulb } from 'lucide-react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import { ChipButton } from './chip-button';
import { type PinnedChip } from './evidence-settings-popover';
import { floatingAskHeidiInputMessages } from './floating-ask-heidi-input.messages';
import { HeidiSettingsButton } from './heidi-settings-button';
import { ProtocolsButton } from './protocols-button';
import type { ProtocolsButtonHandle } from './protocols-button.types';
import { SelectedTextBadge } from './selected-text-badge';
import { useFloatingAskHeidi } from './use-floating-ask-heidi';
import { useSelectedNoteTextContext } from './use-selected-note-text-context';

const TrackedAskHeidiMicTooltipContent = () => {
  useDictateReengagementViewExposure('ask_heidi_mic_tooltip', true);
  useEffect(() => {
    trackDictateReengagementViewed('ask_heidi_mic_tooltip');
  }, []);

  return <DictateShortcutTooltipContent />;
};

const EASE_OUT_QUINT = [0.23, 1, 0.32, 1] as const;
/**
 * How long a control takes to enter or leave the bar's right-hand cluster, and
 * how long Sources takes to slide to the x-position that leaves behind.
 *
 * Same 0.2s / ease-out-quint the peek sheet sitting directly above uses for its
 * collapsed-row crossfade, so the two halves of one card settle together rather
 * than one chasing the other. Kept as a local value rather than imported from
 * the sheet: pulling that module in for a number would put the whole sheet on
 * the composer's import graph.
 */
const CHROME_SWAP_DURATION = 0.2;
// Rotating placeholder cadence: run through the hint set a few times per session
// stage, then settle on the first hint. Slightly slower than a plain carousel so
// each hint is comfortable to read.
export const PLACEHOLDER_CYCLES = 3;
export const PLACEHOLDER_ROTATE_MS = 5200;

/** The Sources trigger for the in-session Work composers: ghost, with Work home's logo stack. */
export const InSessionSourcesButton = ({
  disabled = false,
  analyticsSurface = 'floating_bar',
}: {
  disabled?: boolean;
  analyticsSurface?: EvidenceAskHeidiSurface;
}) => (
  <SourcesButton
    disabled={disabled}
    buttonVariant="ghost"
    popoverSide="top"
    popoverAlign="end"
    hideBadges
    personalisedSourceLogos
    sourceAvatarVariant="work"
    analyticsSurface={analyticsSurface}
  />
);
// `.ProseMirror` lets its content drive its own height (no `min-h`) so the
// parent editor row can vertically center the `<p>` (and the absolute-
// positioned placeholder pseudo) regardless of row height. With a min-h on
// ProseMirror the `<p>` always pinned to the top of a too-tall element,
// pulling the placeholder ~4px above the visual centre line.
const EDITOR_CLASS =
  'w-full min-w-0 [&_.ProseMirror]:max-h-[200px] [&_.ProseMirror]:min-w-0 [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:scrollbar-fade [&_.ProseMirror]:break-words [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child]:relative [&_.ProseMirror_p.is-editor-empty:first-child]:w-full [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:absolute [&_.ProseMirror_p.is-editor-empty:first-child::before]:left-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:right-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:top-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:truncate [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-text-tertiary [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]';
interface FloatingAskHeidiInputProps {
  className?: string;
  onSendMessage?: (message: string) => void;
  onBeforeSend?: () => void;
  disabled?: boolean;
  variant?: 'standalone' | 'embedded';
  firstSuggestedChip?: SuggestedChip | null;
  sheetRef?: React.RefObject<HTMLDivElement>;
  pinnedChips?: PinnedChip[];
  onPinnedChipClick?: (chip: PinnedChip) => void;
  onProtocolsMenuOpenChange?: (open: boolean) => void;
  /**
   * Callback to add a new protocol from marketplace or custom create.
   * `origin` distinguishes 'library' vs 'custom' for analytics.
   */
  onAddProtocol?: (
    label: string,
    query: string,
    origin: 'library' | 'custom',
    description?: string
  ) => void;
  /** Callback to edit an existing custom protocol */
  onEditProtocol?: (
    chipId: string,
    label: string,
    query: string,
    description?: string
  ) => void;
  /** Callback to remove a protocol from marketplace */
  onRemoveProtocol?: (chipId: string) => void;
  /** Callback to restore a default protocol */
  onRestoreDefaultShortcut?: (shortcutId: string) => void;
  /** Whether the protocols shortcut control should be disabled independently of the input. */
  protocolsDisabled?: boolean;
  /** Tooltip to show when the protocols shortcut control is disabled. */
  protocolsDisabledTooltip?: string;
  /**
   * Disables only the controls that need the server — send (both the button and
   * the Enter path), dictate, and the inline suggestion chip — while leaving the
   * composer's local half live.
   *
   * Deliberately separate from `disabled`, which sets `editable: false` on the
   * TipTap editor. Typing, editing and clearing the composer text are local
   * actions that succeed regardless of connectivity, so folding them into the
   * same flag would strand a clinician who typed while connected and then
   * dropped out with text they can neither change nor clear. Splitting the
   * disabled state by action capability, rather than gating a whole mixed
   * local/network group on its most restrictive member, is the standing rule
   * here.
   */
  sendDisabled?: boolean;
  /** Reason shown on the gated send control, so the state is explained rather than silent. */
  sendDisabledTooltip?: string;
  /** Whether the shortcut limit has been reached */
  isAtShortcutLimit?: boolean;
  /** Current shortcut count */
  shortcutCount?: number;
  /** Maximum shortcuts allowed */
  maxShortcuts?: number;
  /** Override the default placeholder text (used by evidence prompts bar experiment) */
  placeholderOverride?: string;
  /**
   * Whether the session has no note yet (pre + during transcription). Drives the
   * stage-aware hint set for the rotating placeholder / guided slash menu.
   */
  isPreNoteSession?: boolean;
  /**
   * Resets the rotating-placeholder cycle whenever it changes (e.g. the session
   * stage). On change the placeholder restarts from the first hint and runs a
   * fresh set of cycles, so each stage (pre/during/post) gets its own rotation.
   */
  placeholderCycleKey?: string;
  /** Show lightbulb icon in idle state to reopen dismissed considerations */
  showReopenLightbulb?: boolean;
  /** Callback to reopen the considerations sheet */
  onReopenConsiderations?: () => void;
  /**
   * When the inline first suggested chip is a document-draft clinical task, invoked before send
   * so the task is dismissed (`is_suggestion_dismissed`) and removed from the strip like peek-sheet clicks.
   */
  onDocumentDraftSuggestionChipClick?: (chip: SuggestedChip) => void;
  /** Override the default voice trigger (`undefined` = live trigger, `null` = hide). */
  voiceTrigger?: ReactNode;
}

export const FloatingAskHeidiInput = forwardRef<
  HTMLDivElement,
  FloatingAskHeidiInputProps
>(
  (
    {
      className,
      onSendMessage,
      onBeforeSend,
      disabled = false,
      variant = 'standalone',
      firstSuggestedChip,
      sheetRef,
      pinnedChips = [],
      onPinnedChipClick,
      onProtocolsMenuOpenChange,
      onAddProtocol,
      onEditProtocol,
      onRemoveProtocol,
      onRestoreDefaultShortcut,
      protocolsDisabled,
      protocolsDisabledTooltip,
      sendDisabled = false,
      sendDisabledTooltip,
      isAtShortcutLimit,
      shortcutCount,
      maxShortcuts,
      placeholderOverride,
      isPreNoteSession = false,
      placeholderCycleKey,
      showReopenLightbulb,
      onReopenConsiderations,
      onDocumentDraftSuggestionChipClick,
      voiceTrigger,
    },
    ref
  ) => {
    const isEmbedded = variant === 'embedded',
      sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
    const isAskHeidiPanelOpen = sidebarOpenTab === SidebarTab.ASK_HEIDI_V2;
    const { isWhiteLabel } = useWhiteLabelSafe();
    const intl = useIntl();
    const showDictationShortcutTooltip = useIsDictateReengagementEnabled();
    const placeholder =
      placeholderOverride ??
      (isWhiteLabel
        ? intl.formatMessage(floatingAskHeidiInputMessages.placeholderWhiteLabel)
        : intl.formatMessage(floatingAskHeidiInputMessages.placeholderV2FloatingBar));
    const placeholderRef = useRef(placeholder);
    placeholderRef.current = placeholder;
    const isPhaseVisible = useAtomValue(floatingAskHeidiInputVisibleAtom);
    const isInlineEditActive = useAtomValue(inlineSelectionEditActiveAtom);
    const setPendingEntryMethod = useSetAtom(pendingEntryMethodAtom);
    const setPendingChipMeta = useSetAtom(pendingChipMetaAtom);
    const containerRef = useRef<HTMLDivElement>(null),
      [hasText, setHasText] = useState(false);
    const [isProtocolsMenuOpen, setIsProtocolsMenuOpen] = useState(false);
    // Guided "/" search + Enter-swallow live behind the sources-shortcuts flag so
    // flag-off / shortcuts-only cohorts keep main's send behaviour.
    const isGuidedShortcutsEnabled = useIsAskHeidiSourcesShortcutsEnabled();
    const guidedShortcutsRef = useRef(isGuidedShortcutsEnabled);
    guidedShortcutsRef.current = isGuidedShortcutsEnabled;
    const shortcutsButtonVisible =
      IS_ASK_HEIDI_SHORTCUTS_ENABLED() || isGuidedShortcutsEnabled;
    const shortcutsButtonVisibleRef = useRef(shortcutsButtonVisible);
    shortcutsButtonVisibleRef.current = shortcutsButtonVisible;
    // Raw editor text, mirrored so we can derive the "/" search query.
    const [inputText, setInputText] = useState('');
    const protocolsRef = useRef<ProtocolsButtonHandle>(null);
    // Everything after a leading "/" drives the shortcut search; empty means
    // browse mode (just "/"). Non-slash text isn't a search.
    const shortcutQuery = inputText.startsWith('/') ? inputText.slice(1) : '';
    const isShortcutSearching = shortcutQuery.trim().length > 0;
    const isShortcutSearchingRef = useRef(isShortcutSearching);
    isShortcutSearchingRef.current = isShortcutSearching;
    const startsWithSlashRef = useRef(false);
    startsWithSlashRef.current = inputText.startsWith('/');
    // Latest menu open/close handler, so the editor's onUpdate (a stale closure)
    // can route slash-triggered opens through the same path as the trigger.
    const handleMenuOpenChangeRef = useRef<(open: boolean) => void>(() => {});
    // Slash-open analytics props — onUpdate is a stale closure, so keep refs.
    const pinnedChipsCountRef = useRef(pinnedChips.length);
    pinnedChipsCountRef.current = pinnedChips.length;
    const isPreNoteSessionRef = useRef(isPreNoteSession);
    isPreNoteSessionRef.current = isPreNoteSession;
    // Rotating placeholder index for the guided Sources + shortcuts experience.
    const [rotatingPlaceholderIndex, setRotatingPlaceholderIndex] = useState(0);
    const prefersReducedMotion = useReducedMotion();

    const prevChipIdRef = useRef<string | null>(null),
      [isNewChip, setIsNewChip] = useState(false);
    const currentChipId = firstSuggestedChip?.id ?? null;
    useEffect(() => {
      if (
        currentChipId &&
        prevChipIdRef.current !== null &&
        currentChipId !== prevChipIdRef.current
      ) {
        setIsNewChip(true);
        const timer = setTimeout(() => setIsNewChip(false), 800);
        return () => clearTimeout(timer);
      }
      prevChipIdRef.current = currentChipId;
    }, [currentChipId]);

    const {
      phase,
      isActive,
      handleActivate,
      handleDeactivate,
      handleSendMessage,
      handleTextChange,
    } = useFloatingAskHeidi({ onSendMessage, isAskHeidiPanelOpen });

    // Selection → Ask Heidi: highlighted note text attached as context
    const {
      hasSelectedTextContext,
      selectedNoteText,
      clearSelectedNoteText,
      setSelectedNoteText,
    } = useSelectedNoteTextContext();

    /**
     * Deactivate and drop the selection context. Used for Escape and
     * click-outside; a fresh highlight in the note re-captures immediately
     * via `useSelectionCapture`.
     */
    const handleDeactivateAndClearSelection = useCallback(() => {
      handleDeactivate();
      setSelectedNoteText(null);
    }, [handleDeactivate, setSelectedNoteText]);

    const phaseRef = useRef(phase),
      handleActivateRef = useRef(handleActivate);
    const isProtocolsMenuOpenRef = useRef(isProtocolsMenuOpen);
    phaseRef.current = phase;
    handleActivateRef.current = handleActivate;
    isProtocolsMenuOpenRef.current = isProtocolsMenuOpen;

    const editor = useEditor({
      extensions: [
        Document,
        Paragraph,
        Text,
        History,
        Placeholder.configure({
          placeholder: () => placeholderRef.current,
          showOnlyWhenEditable: true,
        }),
      ],
      editorProps: {
        attributes: {
          // Keep this element block-level. `display: flex` here turns each <p>
          // into a flex item with min-width: auto and breaks long-paste wrapping.
          class: 'outline-none w-full min-w-0 text-base text-text-primary',
        },
      },
      onUpdate: ({ editor: e }) => {
        const rawText = e.getText();
        setInputText(rawText);
        const text = rawText.trim().length > 0;
        setHasText(text);
        handleTextChange(text);
        const openViaSlash = () => {
          // Controlled `open` skips Radix onOpenChange, so slash opens never
          // hit ProtocolsButton's trigger tracking — emit here instead.
          track({
            event_name: TrackingEventName.EvidenceShortcutsOpened,
            props: {
              shortcut_count: pinnedChipsCountRef.current,
              is_pre_note_session: isPreNoteSessionRef.current,
              open_method: 'slash',
            },
            options: { amplitude: false },
          });
          handleMenuOpenChangeRef.current(true);
        };
        if (guidedShortcutsRef.current) {
          // Guided menu: stay open while text starts with "/" (search mode),
          // close once it doesn't.
          const startsWithSlash = rawText.startsWith('/');
          if (startsWithSlash && !isProtocolsMenuOpenRef.current) {
            openViaSlash();
          } else if (!startsWithSlash && isProtocolsMenuOpenRef.current) {
            handleMenuOpenChangeRef.current(false);
          }
        } else if (shortcutsButtonVisibleRef.current) {
          // Legacy: open only on a bare "/" (matches pre-guided behaviour).
          if (rawText === '/' && !isProtocolsMenuOpenRef.current) {
            openViaSlash();
          }
        }
      },
      onFocus: () => {
        if (phaseRef.current === 'idle') handleActivateRef.current();
      },
      editable: !disabled,
      immediatelyRender: false,
    });

    useEffect(() => {
      if (isActive && editor && !editor.isFocused) {
        const t = setTimeout(() => editor.commands.focus('end'), 50);
        return () => clearTimeout(t);
      }
    }, [isActive, editor]);

    // GRO-3269: consume an externally requested prefill (improve-notes Ask
    // Heidi spotlight). Inserts the text ready to edit/send — never sends.
    const [prefill, setPrefill] = useAtom(floatingAskHeidiPrefillAtom);
    useEffect(() => {
      if (!prefill || !editor || disabled) return;
      setPrefill(null);
      if (phaseRef.current === 'idle') handleActivateRef.current();
      // Focus synchronously within the chain. Deferring with setTimeout breaks:
      // setPrefill(null) flips the `prefill` dep, so the prior run's cleanup
      // clears the timer before it fires and the re-run early-returns.
      editor.chain().clearContent().insertContent(prefill).focus('end').run();
    }, [prefill, editor, disabled, setPrefill]);

    // Selection → Ask Heidi "Ask anything": activate + focus the bar so the
    // user can type their question against the highlighted text. Reset to 0
    // after consuming so remounts do not re-focus unprompted (one-shot).
    const [focusTrigger, setFocusTrigger] = useAtom(floatingBarFocusTriggerAtom);
    useEffect(() => {
      if (focusTrigger === 0 || !editor || disabled) return;
      setFocusTrigger(0);
      if (phaseRef.current === 'idle') handleActivateRef.current();
      editor.commands.focus('end');
    }, [focusTrigger, editor, disabled, setFocusTrigger]);
    // Note: We intentionally do NOT clear editor content when phase becomes 'idle'.
    // This preserves user's typed text when they click outside (e.g., into the note area).
    // Content is naturally reset when the component remounts after sidebar closes.

    useEffect(() => {
      if (!isActive) return;
      const onClickOutside = (e: MouseEvent) => {
        const t = e.target as Node;
        // Check if click is inside a Radix UI portaled element (popover, dialog, dropdown, etc.)
        const isInsideRadixPortal =
          t instanceof Element &&
          (t.closest('[data-radix-popper-content-wrapper]') ||
            t.closest('[data-radix-portal]') ||
            t.closest('[role="dialog"]'));
        if (isInsideRadixPortal) return;
        if (!containerRef.current?.contains(t) && !sheetRef?.current?.contains(t))
          handleDeactivateAndClearSelection();
      };
      const timer = setTimeout(
        () => document.addEventListener('mousedown', onClickOutside),
        100
      );
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', onClickOutside);
      };
    }, [isActive, handleDeactivateAndClearSelection, sheetRef]);

    const sendMessage = useCallback(() => {
      // The one choke point both the send button and the Enter path go through.
      // Guarding here rather than at each call site keeps a gated send from
      // staging a message the receiving panel will never pick up: the panel's
      // own composer is gated by the same condition and skips its pending-message
      // hand-off, so an ungated send would open the panel to nothing and leave
      // the message stranded.
      if (sendDisabled || !hasText || !editor) return;
      const msg = editor.getText().trim();
      if (msg) {
        onBeforeSend?.();
        // With highlighted note text attached, scope the outgoing message to
        // that selection. The input keeps showing only what the user typed.
        if (hasSelectedTextContext && selectedNoteText) {
          handleSendMessage(
            composeSelectedTextQuestionPrompt(msg, selectedNoteText.text)
          );
          setSelectedNoteText(null);
        } else {
          handleSendMessage(msg);
        }
      }
    }, [
      sendDisabled,
      hasText,
      editor,
      handleSendMessage,
      onBeforeSend,
      hasSelectedTextContext,
      selectedNoteText,
      setSelectedNoteText,
    ]);

    // Clicking anywhere on the bar chrome activates it (focus → the peek sheet
    // opens). Previously activation relied solely on the editor's `onFocus`,
    // which only fires on a pixel-perfect hit of the editable line — padding
    // clicks focused nothing. Drive activation directly off the click
    // (idempotent — `handleActivate` no-ops unless idle/returning) and also
    // focus the editor for typing. Controls and the editable keep their own
    // behaviour so we don't hijack a button press or fight caret placement.
    const handleBarMouseDown = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return;
        const target = e.target as HTMLElement;
        if (
          target.closest('button, a, input, textarea, [role="button"], [role="menuitem"]')
        ) {
          return;
        }
        handleActivate();
        if (editor && !editor.isFocused && !target.closest('[contenteditable]')) {
          editor.commands.focus();
        }
      },
      [disabled, editor, handleActivate]
    );

    // Guided "/" menu: Radix focuses remounted menuitems after each query
    // keystroke/backspace — return the caret to TipTap so Backspace keeps working.
    const retainEditorFocus = useCallback(() => {
      if (
        isEditorLive(editor, 'floating-ask-heidi.retainEditorFocus') &&
        !editor.isFocused
      ) {
        editor.commands.focus('end');
      }
    }, [editor]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isComposingEvent(e.nativeEvent)) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          handleDeactivateAndClearSelection();
          return;
        }
        // "/" command mode (guided menu only): arrows move highlight, Enter runs
        // match; bare "/" / no-match swallows Enter so raw "/command" isn't sent.
        if (guidedShortcutsRef.current && startsWithSlashRef.current) {
          const protocols = protocolsRef.current;
          if (isShortcutSearchingRef.current && protocols?.hasResults()) {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              protocols.moveHighlight(1);
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              protocols.moveHighlight(-1);
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (protocols.runHighlighted()) {
                handleMenuOpenChangeRef.current(false);
              }
              return;
            }
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            return;
          }
        }
        if (e.key === 'Enter' && !e.shiftKey && hasText) {
          e.preventDefault();
          sendMessage();
        }
      },
      [handleDeactivateAndClearSelection, hasText, sendMessage]
    );

    const handleChipClick = useCallback(
      (chip: SuggestedChip) => {
        // Chips reach `handleSendMessage` without passing through `sendMessage`,
        // so they need the same guard. The chip itself is withheld while gated
        // (below), making this the belt to that braces rather than a silent
        // no-op the user can reach.
        if (sendDisabled) return;
        const isPinned = chip.isPermanent ?? false;
        if (!isPinned && chip.category === 'document_draft') {
          onDocumentDraftSuggestionChipClick?.(chip);
        }
        track({
          event_name: TrackingEventName.ButtonClicked,
          props: {
            action: isPinned
              ? ButtonAction.AskHeidiShortcutChipClicked
              : ButtonAction.AskHeidiSuggestionChipClicked,
            label: isPinned ? 'shortcut_chip' : 'suggestion_chip',
            chip_label: chip.label,
            chip_category: chip.category,
            is_pinned: isPinned,
            click_source: 'floating_input',
          },
        });
        const isDocumentDraft = chip.category === 'document_draft';
        setPendingEntryMethod(
          isPinned
            ? 'shortcut_chip'
            : isDocumentDraft
              ? 'document_suggestion_chip'
              : 'suggestion_chip'
        );
        // Pinned chips in this surface are PinnedChip at runtime (SuggestedChip
        // with extra analytics fields). Narrow via cast to read `origin`.
        const pinnedOrigin = isPinned ? (chip as PinnedChip).origin : undefined;
        setPendingChipMeta({
          label: chip.label,
          category: isDocumentDraft ? 'document_suggestion' : chip.category,
          origin: pinnedOrigin,
          surface: 'floating_input',
        });
        onBeforeSend?.();
        handleSendMessage(chip.query);
      },
      [
        sendDisabled,
        handleSendMessage,
        onBeforeSend,
        setPendingEntryMethod,
        setPendingChipMeta,
        onDocumentDraftSuggestionChipClick,
      ]
    );

    const handleProtocolsMenuOpenChange = useCallback(
      (open: boolean) => {
        setIsProtocolsMenuOpen(open);
        onProtocolsMenuOpenChange?.(open);
        if (open) {
          if (phaseRef.current === 'idle') handleActivateRef.current();
          // Only seed "/" when opening from an empty editor (e.g. the trigger
          // button). If the user typed "/prep" to open the menu, leave it be so
          // the search query is preserved.
          if (editor && editor.getText().trim() === '') {
            editor.chain().clearContent().insertContent('/').run();
            setInputText('/');
          }
          // Guided only: keep caret in TipTap so "/prep" typing works (menu is
          // modal={false} + open autofocus prevented under the same flag).
          if (isGuidedShortcutsEnabled) {
            editor?.commands.focus('end');
          }
        } else {
          // Closing (selection / escape / outside click): drop in-progress
          // slash text. Guided clears any "/…" search; legacy only clears a
          // bare "/" so typed "/prep" (sendable in that cohort) is preserved.
          const text = editor?.getText() ?? '';
          const shouldClear = isGuidedShortcutsEnabled
            ? text.startsWith('/')
            : text.trim() === '/';
          if (shouldClear) {
            editor?.commands.clearContent();
            setHasText(false);
            setInputText('');
          }
          editor?.commands.focus('end');
        }
      },
      [editor, isGuidedShortcutsEnabled, onProtocolsMenuOpenChange]
    );
    handleMenuOpenChangeRef.current = handleProtocolsMenuOpenChange;

    useManualExposure({
      flagKey: 'release-ask-heidi-sources-shortcuts',
      isEnabled: isGuidedShortcutsEnabled,
      isVisible: isPhaseVisible && !isAskHeidiPanelOpen,
    });

    const useRotatingPlaceholder = isGuidedShortcutsEnabled;
    const rotatingPlaceholders = useMemo(() => {
      const base = intl.formatMessage(
        isPreNoteSession
          ? floatingAskHeidiInputMessages.placeholderAskAnything
          : floatingAskHeidiInputMessages.placeholderAskEditCreate
      );
      const shortcutsHint = intl.formatMessage(
        floatingAskHeidiInputMessages.placeholderTypeForShortcuts
      );
      // Pre-note nudges toward prepping; post-note drops the "/ to prep" hint.
      return isPreNoteSession
        ? [
            base,
            intl.formatMessage(floatingAskHeidiInputMessages.placeholderTypeToPrep),
            shortcutsHint,
          ]
        : [base, shortcutsHint];
    }, [intl, isPreNoteSession]);

    /**
     * A hovered suggestion or level-2 starter previews its phrasing on the
     * placeholder line, and that copy carries its variable slots as data —
     * "Does this patient qualify for [programme or funding]?". TipTap's
     * placeholder is a CSS pseudo-element, which can only draw flat text, so
     * the preview draws as an overlay instead; `renderPlaceholderSlots` then
     * puts a badge where the brackets were.
     *
     * A preview also outranks the rotating hint set. Both want the same line,
     * and a hint cycling on its own timer is not what the clinician is
     * pointing at. Callers must only pass a hover (or prompts-bar) string
     * here — never the standing stage hint, or rotation never starts.
     */
    const previewPlaceholder =
      placeholderOverride !== undefined && placeholderOverride !== ''
        ? placeholderOverride
        : null;
    const rotatingPlaceholder = useRotatingPlaceholder
      ? rotatingPlaceholders[rotatingPlaceholderIndex]
      : undefined;
    const overlayPlaceholder = previewPlaceholder ?? rotatingPlaceholder;
    // While the animated overlay is showing, blank out the tiptap placeholder so
    // the two don't stack.
    placeholderRef.current = overlayPlaceholder === undefined ? placeholder : '';

    // Carousel the rotating placeholder for a few cycles, then settle on the
    // first hint. Restart from the top whenever rotation toggles, the hint set
    // changes, or the session stage changes (placeholderCycleKey) so each stage
    // gets its own fresh set of cycles.
    useEffect(() => {
      setRotatingPlaceholderIndex(0);
      const count = rotatingPlaceholders.length;
      if (!useRotatingPlaceholder || count <= 1) return;
      const maxSteps = count * PLACEHOLDER_CYCLES; // lands back on the first hint
      let step = 0;
      const id = setInterval(() => {
        step += 1;
        setRotatingPlaceholderIndex(step % count);
        if (step >= maxSteps) clearInterval(id);
      }, PLACEHOLDER_ROTATE_MS);
      return () => clearInterval(id);
    }, [useRotatingPlaceholder, rotatingPlaceholders.length, placeholderCycleKey]);

    // Force ProseMirror to recompute its placeholder decoration when the
    // effective placeholder changes (e.g. toggling rotation on/off), since the
    // extension only re-reads `placeholderRef` on a transaction.
    useEffect(() => {
      if (editor) editor.view.dispatch(editor.state.tr);
    }, [editor, useRotatingPlaceholder, placeholder]);

    /**
     * Which face the one primary-action control is wearing.
     *
     * Activation is the hinge — see the block comment on the slot below.
     *
     * `|| phase === 'sending'` is not decoration. `isActive` is
     * `focused || typing`, so submitting drops it, and on the bare condition
     * the control would flip back to voice in the very frame the clinician
     * pressed send and then fade out wearing the wrong face — with the settle
     * animation drawing the eye to it. The message is on its way; send is
     * still what the button did.
     */
    const showSend = isActive || phase === 'sending';
    const morphFace = showSend ? 'send' : 'voice';
    // A mount is not a morph. Without this the voice face would settle on the
    // composer's first paint, where nothing has changed and there is nothing to
    // explain to the eye.
    const lastMorphFaceRef = useRef(morphFace);
    const isMorphing = lastMorphFaceRef.current !== morphFace;
    lastMorphFaceRef.current = morphFace;

    if (isAskHeidiPanelOpen || !isPhaseVisible) return null;
    const isIdle = phase === 'idle',
      isSending = phase === 'sending';
    /**
     * Both the slide and the enter/exit pop are `transform`, which the reduced-
     * motion contract says to drop rather than shorten — so under that
     * preference Sources takes its new x on the next paint and the controls
     * cross-fade in place. `'position'` (not `true`) because nothing here
     * changes size; animating width as well would squash the button faces.
     */
    const chromeLayout = prefersReducedMotion ? false : ('position' as const);
    const chromeEnterExit = {
      opacity: 0,
      scale: prefersReducedMotion ? 1 : 0.9,
    };
    /**
     * The morph's settle. Deliberately not a fade and emphatically not a
     * crossfade: a fade leaves the slot momentarily near-empty, and a crossfade
     * leaves both faces of the control on screen at once, which is the exact
     * reading — "which of these two things will my click do?" — that replacing
     * the swap with a morph is meant to remove. The incoming face is opaque
     * from its first frame and only springs to size.
     *
     * `false` under reduced motion resolves the new face instantly at its end
     * state rather than animating it briefly, which is what that preference
     * asks for on a `transform`.
     */
    const morphSettle = prefersReducedMotion || !isMorphing ? false : { scale: 0.85 };
    const isHidden = isSending || isInlineEditActive;
    // Declared once so the gated and ungated branches below render the same
    // button, differing only in whether it is wrapped in its reason tooltip.
    const sendButton = (
      <Button
        type="button"
        variant="default"
        size="md"
        iconOnly
        disabled={disabled || sendDisabled || !hasText}
        onClick={sendMessage}
        aria-label={intl.formatMessage(floatingAskHeidiInputMessages.sendMessage)}
        className="bg-background-primary text-text-primary-invert"
        data-testid="ask-heidi-submit-button"
      >
        <ArrowUp size={12} strokeWidth={2.5} />
      </Button>
    );
    const voiceFace =
      voiceTrigger !== undefined ? voiceTrigger : <VoiceAssistantTrigger />;
    const sendFace = sendDisabled ? (
      <Tooltip>
        <TooltipTrigger asChild>
          {/*
            A disabled button is out of the tab order, which would put the
            explanation out of keyboard reach, so the wrapper takes the tab stop.
            It exists only on this branch: ungated the button is focusable itself
            and a stop here would double up.
          */}
          <span className="inline-flex" tabIndex={0} data-testid="ask-heidi-send-gate">
            {sendButton}
          </span>
        </TooltipTrigger>
        {sendDisabledTooltip && (
          <TooltipContent side="top" align="center" className="max-w-xs">
            {sendDisabledTooltip}
            <TooltipArrow />
          </TooltipContent>
        )}
      </Tooltip>
    ) : (
      // Ungated there is nothing to explain, so the button renders bare.
      // Wrapping unconditionally would put a `TooltipProvider` requirement (and
      // a redundant span) on every consumer of this composer for a tooltip that
      // can never open.
      sendButton
    );
    return (
      <m.div
        ref={ref}
        className={cn(
          'mx-auto w-full max-w-screen-md shrink-0 pb-4',
          // Slide out of the way while the inline selection panel is up
          isInlineEditActive && 'pointer-events-none',
          className
        )}
        initial={false}
        animate={{
          opacity: isHidden ? 0 : 1,
          y: isSending ? 8 : isInlineEditActive ? 24 : 0,
        }}
        transition={{ duration: 0.3, ease: EASE_OUT_QUINT }}
      >
        <div
          ref={containerRef}
          data-testid="floating-ask-heidi-container"
          className={cn(
            'group/input relative z-20 flex items-center gap-2 px-4 py-3',
            !isEmbedded && [
              'rounded-xl border shadow-chat transition-colors duration-200',
              isActive
                ? 'border-border-pressed'
                : 'border-border hover:border-border-hover',
            ],
            'bg-surface'
          )}
          onKeyDown={handleKeyDown}
          onMouseDown={isGuidedShortcutsEnabled ? handleBarMouseDown : undefined}
        >
          {shortcutsButtonVisible ? (
            <ProtocolsButton
              ref={protocolsRef}
              pinnedChips={pinnedChips}
              onPinnedChipClick={onPinnedChipClick}
              disabled={disabled || protocolsDisabled}
              disabledTooltip={protocolsDisabledTooltip}
              open={isProtocolsMenuOpen}
              onOpenChange={handleProtocolsMenuOpenChange}
              onAddProtocol={onAddProtocol}
              onEditProtocol={onEditProtocol}
              onRemoveProtocol={onRemoveProtocol}
              onRestoreDefaultShortcut={onRestoreDefaultShortcut}
              isAtShortcutLimit={isAtShortcutLimit}
              shortcutCount={shortcutCount}
              maxShortcuts={maxShortcuts}
              isPreNoteSession={isPreNoteSession}
              searchQuery={isGuidedShortcutsEnabled ? shortcutQuery : undefined}
              onRetainInputFocus={
                isGuidedShortcutsEnabled ? retainEditorFocus : undefined
              }
            />
          ) : (
            <HeidiSettingsButton />
          )}
          {hasSelectedTextContext && (
            <SelectedTextBadge onRemove={clearSelectedNoteText} />
          )}
          <div
            className="relative flex min-h-9 min-w-0 flex-1 items-center"
            data-testid="ask-heidi-editor"
          >
            <EditorContent editor={editor} className={cn(EDITOR_CLASS)} />
            {overlayPlaceholder !== undefined && !hasText && (
              <div
                className="pointer-events-none absolute inset-0 flex items-center overflow-hidden"
                // A preview follows the pointer down the starter list. An
                // accessible name that rewrote itself on mouse-move would be
                // noise, so only the eye reads it — the rotating hint keeps
                // being announced as before.
                aria-hidden={previewPlaceholder !== null || undefined}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <m.span
                    key={previewPlaceholder ?? `rotating-${rotatingPlaceholderIndex}`}
                    initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -4 }}
                    transition={{ duration: 0.28, ease: EASE_OUT_QUINT }}
                    className="truncate text-base text-text-tertiary"
                    data-testid="ask-heidi-placeholder-overlay"
                  >
                    {previewPlaceholder === null
                      ? overlayPlaceholder
                      : renderPlaceholderSlots(previewPlaceholder)}
                  </m.span>
                </AnimatePresence>
              </div>
            )}
          </div>
          <div className="flex min-h-9 shrink-0 items-center gap-2">
            {isIdle && showReopenLightbulb && onReopenConsiderations && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                onClick={onReopenConsiderations}
                aria-label={intl.formatMessage({
                  defaultMessage: 'Show considerations',
                  id: 'FPg3L3bWQ4',
                })}
              >
                <Lightbulb className="size-4 text-text-secondary" />
              </Button>
            )}
            {isIdle &&
              IS_TRANSCRIPT_CHIPS_ENABLED() &&
              !isGuidedShortcutsEnabled &&
              // Withheld rather than shown-and-inert: a suggestion is an offer,
              // and offering one that cannot be acted on is the same dead click
              // the send gate exists to remove.
              !sendDisabled && (
                <AnimatePresence>
                  {firstSuggestedChip && (
                    <m.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <ChipButton
                        chip={firstSuggestedChip}
                        index={0}
                        onChipClick={handleChipClick}
                        size="sm"
                        variant="outline"
                        iconProPurple
                        isNew={isNewChip}
                      />
                    </m.div>
                  )}
                </AnimatePresence>
              )}
            {/*
              Sources is chrome, not an action belonging to one state: which
              corpus the composer will search is true of the bar whether or not
              anyone is typing into it, so it renders identically in both. One
              element with one set of props, rather than a pair of branches —
              that is what lets React keep the node across the state change so
              `layout` can carry it to its new x instead of it jumping there
              when the controls beside it are swapped.

              Deliberately not conditioned on the capability-signalling arm.
              The arms change what the sheet ABOVE the bar says; the bar's own
              chrome is the constant they are measured against, the same way
              the sheet's `collapsedRightSlot` is passed on every arm.

              Personalised logos stay behind the guided-shortcuts flag so the
              floating bar matches the sidebar (GRO-2897) and the off-cohort
              keeps a remote kill switch.
            */}
            <m.div
              layout={chromeLayout}
              transition={{ duration: CHROME_SWAP_DURATION, ease: EASE_OUT_QUINT }}
              className="flex shrink-0 items-center"
            >
              <SourcesButton
                disabled={disabled}
                popoverSide="top"
                popoverAlign="end"
                hideBadges
                personalisedSourceLogos={isGuidedShortcutsEnabled}
                analyticsSurface="floating_bar"
              />
            </m.div>
            {/*
              `popLayout` takes dictate out of layout flow as it fades, so the
              row closes up and Sources slides during the swap rather than after
              it. Dictate is the only control that still enters and leaves:
              it is not a rival to send, it FEEDS send, so it has no business
              being a face of the same control.
            */}
            <AnimatePresence initial={false} mode="popLayout">
              {isIdle && (
                <m.div
                  key="dictate"
                  layout={chromeLayout}
                  initial={chromeEnterExit}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={chromeEnterExit}
                  transition={{ duration: CHROME_SWAP_DURATION, ease: EASE_OUT_QUINT }}
                  className="flex size-9 shrink-0 items-center justify-center"
                >
                  <DictateButton
                    key="ask-heidi-dictate"
                    disabled={disabled || sendDisabled}
                    size="md"
                    editor={editor}
                    idleTooltipContent={
                      showDictationShortcutTooltip ? (
                        <TrackedAskHeidiMicTooltipContent />
                      ) : undefined
                    }
                  />
                </m.div>
              )}
            </AnimatePresence>
            {/*
              The bar's primary action, as ONE control wearing one of two faces.
              Voice and send are the same slot because they are the same
              question — "where is this query going?" — answered for the mode
              the clinician has chosen, and they can never both be answers at
              once.

              The hinge is ACTIVATION. The text-versus-voice choice is made at
              first interaction, and clicking into the composer IS that choice;
              once it is made, voice is not being taken away, it is the branch
              not taken. Send then does what a composer's send has always done
              here — show where the query is going, disabled until there is a
              query to send. (A withheld-until-usable rule is right for the
              inline suggestion chip beside this, but that is an OFFER the
              clinician did not ask for; send is the destination of an action
              they have already committed to.)

              Hinging here also keeps the row a constant width. Dictate leaves
              and send arrives in the same beat, two `size-9` cells trading
              places in a `gap-2` row, so there is no intermediate state with
              one fewer control for Sources to slide across and back.

              Exactly one face is mounted at a time, and the swap is atomic:
              there is no `AnimatePresence` here, so React unmounts the outgoing
              face in the same commit that mounts the incoming one. The label,
              the handler, the disabled state and the glyph therefore cannot
              drift apart, because they are never sourced from two renders at
              once. That is the invariant a morph has to buy, and an exit
              animation is precisely what would sell it back.

              The slot collapses when NEITHER face renders, which is a real
              state: `VoiceAssistantTrigger` self-gates on flag + auth and
              returns null, and the `voiceTrigger` prop can be handed `null`
              outright. `has-[[data-morph-face]:empty]` asks the DOM what
              actually rendered, which is the only honest way to know — the
              trigger self-gates precisely so callers need not, so re-deriving
              its condition here would drift the first time that gate changed,
              and would be wrong for the prop path in any case, where this
              component has no idea what it was handed.

              The rule needs no state condition, and that is a property of the
              hinge above rather than luck. The bar renders in five phases
              (`floatingAskHeidiInputVisibleAtom`): `focused`, `typing` and
              `sending` all wear the send face, which is never empty, so the
              rule cannot fire in any of them. It is reachable only from `idle`
              and `returning` — the two at-rest phases, where a collapsed
              trailing cell is exactly what is wanted. Adding an `isIdle` guard
              would be dead weight that hid that reasoning.

              `hidden`, not a zero width: the row is `gap-2`, and a zero-width
              flex item still donates its gap. Deliberately no `layout` on this
              element either — it is the last child of a row whose right edge is
              pinned by the editor's `flex-1`, so it never moves and has nothing
              to animate, and a `layout` node that can go `display: none`
              measures an empty rect and flies back in from it. Sources keeps
              its slide and is never hidden, so the row still closes up around
              this when it collapses.
            */}
            <div
              className="flex size-9 shrink-0 items-center justify-center has-[[data-morph-face]:empty]:hidden"
              data-testid="ask-heidi-primary-action"
            >
              <m.div
                key={morphFace}
                initial={morphSettle}
                animate={{ scale: 1 }}
                transition={{ duration: CHROME_SWAP_DURATION, ease: EASE_OUT_QUINT }}
                className="flex size-9 items-center justify-center"
                data-morph-face={morphFace}
              >
                {showSend ? sendFace : voiceFace}
              </m.div>
            </div>
          </div>
        </div>
      </m.div>
    );
  }
);
FloatingAskHeidiInput.displayName = 'FloatingAskHeidiInput';
